/**
 * Generative music player. Each theme plays on its own *deck* (identity,
 * step clock, fader). A theme change starts a new deck at the current deck's
 * next bar boundary and crossfades the two over 1.5 s; the old deck keeps
 * playing its own tune while it fades, so nothing is cut mid-note.
 *
 * Per eighth-note step the current phrase (motif.ts) is rendered: lead melody,
 * a pad chord and a soft bass note on each bar, and — when the stack is high —
 * the tension layer (a soft shaker plus a sustained fifth), routed to its own
 * gain node so it can never exceed its +3 dB budget.
 */
import type { ThemeAudio } from '@dropstack/core';
import { ParamTrack, writeAutomation } from './automation.ts';
import type { MixGraph } from './graph.ts';
import { MELODY_RANGE, PHRASE_STEPS, buildPhrase, chordTones, createMusicIdentity, type MelodyNote, type MusicIdentity, type Phrase } from './motif.ts';
import { REGISTER, degreeToMidi, midiToHz, pitchClass, rootMidi } from './pitch.ts';
import { STEPS_PER_BAR, StepClock, type ScheduledStep } from './scheduler.ts';
import { playNoiseGrain, playVoice } from './synth.ts';
import { BASS_VOICE, LEAD_VOICES, PAD_VOICES, PART_VELOCITY, TENSION_PAD_VOICE, TENSION_SHAKER, sanitizeThemeAudio } from './timbres.ts';

/** Crossfade between themes (s). */
export const THEME_CROSSFADE_S = 1.5;
/** Fade-in when music starts / fade-out when it stops (s). */
export const MUSIC_START_FADE_S = 1;
export const MUSIC_STOP_FADE_S = 0.8;
/** How long a silent, retired deck is kept before it is disconnected. */
const RETIRE_GRACE_S = 0.25;
/** Below this intensity-layer level, tension notes are not even scheduled (saves CPU). */
const TENSION_EPSILON = 0.001;
/** Golden-ratio stride through the noise buffer, so successive shaker grains never repeat. */
const GRAIN_STRIDE_S = 0.618;

interface Deck {
  audio: ThemeAudio;
  identity: MusicIdentity;
  clock: StepClock;
  leadRoot: number;
  padRoot: number;
  bassRoot: number;
  out: GainNode;
  fader: ParamTrack;
  /** Audio time from which this deck schedules nothing more (null = live). */
  retireAt: number | null;
  phrase: Phrase | null;
  melodyByStep: Map<number, MelodyNote>;
}

/** Two themes that would sound identical need no crossfade. */
function sameMusic(a: ThemeAudio, b: ThemeAudio): boolean {
  return pitchClass(a.key) === pitchClass(b.key) && a.scale === b.scale && a.bpm === b.bpm && a.lead === b.lead && a.pad === b.pad;
}

/** Fold a MIDI note into [low, low + span] by octaves (smooth chord voicing). */
function foldInto(midi: number, low: number, span: number): number {
  let m = midi;
  while (m > low + span) m -= 12;
  while (m < low) m += 12;
  return m;
}

export class MusicPlayer {
  private decks: Deck[] = [];
  private grainOffset = 0;

  constructor(private readonly g: MixGraph) {}

  /** True while any deck exists (including ones still fading out). */
  get active(): boolean {
    return this.decks.length > 0;
  }

  /** True while music is playing or scheduled (not fading out). */
  get playing(): boolean {
    return this.decks.some((d) => d.retireAt === null);
  }

  /** Start music at audio time `at` with a gentle fade-in. No-op if already playing. */
  start(audio: ThemeAudio, at: number): void {
    if (this.playing) return;
    const deck = this.createDeck(audio, at);
    this.fade(deck, 1, at, at + MUSIC_START_FADE_S);
  }

  /** Fade all music out; decks are disconnected once silent. */
  stop(): void {
    const now = this.g.ctx.currentTime;
    for (const d of this.decks) {
      if (d.retireAt !== null && d.retireAt <= now + MUSIC_STOP_FADE_S) continue;
      this.fade(d, 0, now, now + MUSIC_STOP_FADE_S);
      d.retireAt = now + MUSIC_STOP_FADE_S;
    }
  }

  /**
   * Switch to `audio` with a crossfade starting at the next bar boundary.
   * Returns the audio time at which the switch starts (now if nothing changes).
   */
  setTheme(audio: ThemeAudio): number {
    const now = this.g.ctx.currentTime;
    const next = sanitizeThemeAudio(audio);
    const live = this.decks.filter((d) => d.retireAt === null);
    const current = live[live.length - 1];
    if (!current) return now;

    // A deck that is queued (start or switch) but has not produced a note yet.
    const queued = current.clock.nextStep === 0 && current.clock.startTime > now;
    if (sameMusic(current.audio, next)) return queued ? current.clock.startTime : now;
    if (queued) {
      // Replace it in place: same start time, same fade (a start fade-in or a crossfade).
      const at = current.clock.startTime;
      const fadeEnd = current.fader.planned.at(-1)?.time ?? at + THEME_CROSSFADE_S;
      this.discard(current);
      const deck = this.createDeck(next, at);
      this.fade(deck, 1, at, Math.max(at, fadeEnd));
      return at;
    }

    const { time: at } = current.clock.nextBarAtOrAfter(now);
    for (const d of live) {
      this.fade(d, 0, at, at + THEME_CROSSFADE_S);
      d.retireAt = at + THEME_CROSSFADE_S;
    }
    const deck = this.createDeck(next, at);
    this.fade(deck, 1, at, at + THEME_CROSSFADE_S);
    return at;
  }

  /**
   * Scheduler callback: render every step that starts before `until`.
   * `tension` is the current intensity-layer gain (0 = skip tension notes).
   */
  tick(now: number, until: number, tension: number): void {
    const newest = this.decks[this.decks.length - 1];
    for (const deck of this.decks) {
      const limit = deck.retireAt === null ? until : Math.min(until, deck.retireAt);
      const withTension = deck === newest && deck.retireAt === null && tension > TENSION_EPSILON;
      for (const step of deck.clock.collect(now, limit)) this.renderStep(deck, step, withTension);
    }
    this.decks = this.decks.filter((d) => {
      if (d.retireAt !== null && now > d.retireAt + RETIRE_GRACE_S) {
        this.discard(d);
        return false;
      }
      return true;
    });
  }

  /** Disconnect everything immediately (engine disposal). */
  dispose(): void {
    for (const d of this.decks) this.discard(d);
    this.decks = [];
  }

  private createDeck(audio: ThemeAudio, at: number): Deck {
    const a = sanitizeThemeAudio(audio);
    const out = this.g.ctx.createGain();
    out.gain.value = 0;
    out.connect(this.g.musicIn);
    const deck: Deck = {
      audio: a,
      identity: createMusicIdentity(a),
      clock: new StepClock(at, a.bpm),
      leadRoot: rootMidi(a.key, REGISTER.lead) + 12 * LEAD_VOICES[a.lead].octave,
      padRoot: rootMidi(a.key, REGISTER.pad),
      bassRoot: rootMidi(a.key, REGISTER.bass),
      out,
      fader: new ParamTrack(0),
      retireAt: null,
      phrase: null,
      melodyByStep: new Map(),
    };
    this.decks.push(deck);
    return deck;
  }

  private discard(deck: Deck): void {
    try {
      deck.out.disconnect();
    } catch {
      // Already disconnected.
    }
    this.decks = this.decks.filter((d) => d !== deck);
  }

  private fade(deck: Deck, to: number, start: number, end: number): void {
    const now = this.g.ctx.currentTime;
    writeAutomation(deck.out.gain, now, deck.fader.rampTo(to, now, start, end));
  }

  private phraseFor(deck: Deck, index: number): Phrase {
    if (!deck.phrase || deck.phrase.index !== index) {
      deck.phrase = buildPhrase(deck.identity, index);
      deck.melodyByStep = new Map(deck.phrase.melody.map((n) => [n.step, n]));
    }
    return deck.phrase;
  }

  private renderStep(deck: Deck, { step, time }: ScheduledStep, withTension: boolean): void {
    const { ctx, noise } = this.g;
    const inPhrase = step % PHRASE_STEPS;
    const phrase = this.phraseFor(deck, Math.floor(step / PHRASE_STEPS));
    const stepS = deck.clock.secondsPerStep;
    const barS = deck.clock.secondsPerBar;
    const { scale } = deck.audio;

    if (inPhrase % STEPS_PER_BAR === 0) {
      const chordRoot = phrase.chords[inPhrase / STEPS_PER_BAR] ?? 0;
      this.renderBar(deck, chordRoot, time, barS, withTension);
    }
    if (deck.audio.pad === 'none' && inPhrase % STEPS_PER_BAR === 4) {
      // Without a pad, a second soft bass pulse on beat 3 keeps the music from sounding thin.
      const chordRoot = phrase.chords[Math.floor(inPhrase / STEPS_PER_BAR)] ?? 0;
      this.renderBass(deck, chordRoot, time, barS * 0.4, PART_VELOCITY.bassPulse);
    }

    const note = deck.melodyByStep.get(inPhrase);
    if (note) {
      const degree = Math.min(MELODY_RANGE.max, Math.max(MELODY_RANGE.min, note.degree));
      playVoice(
        ctx,
        deck.out,
        LEAD_VOICES[deck.audio.lead],
        { hz: midiToHz(degreeToMidi(degree, deck.leadRoot, scale)), time, dur: note.length * stepS * 0.95, velocity: note.velocity },
        noise.white,
      );
    }

    if (withTension) {
      // Soft shaker: brighter on the off-beats, like a gentle egg shaker.
      this.grainOffset = (this.grainOffset + GRAIN_STRIDE_S) % noise.white.duration;
      playNoiseGrain(ctx, this.g.intensityIn, noise.white, {
        time,
        dur: TENSION_SHAKER.durS,
        gain: step % 2 === 1 ? TENSION_SHAKER.offbeatGain : TENSION_SHAKER.gain,
        attack: TENSION_SHAKER.attackS,
        filter: TENSION_SHAKER.filter,
        offsetS: this.grainOffset,
      });
    }
  }

  private renderBar(deck: Deck, chordRoot: number, time: number, barS: number, withTension: boolean): void {
    const { ctx } = this.g;
    const { scale, pad } = deck.audio;
    const tones = chordTones(chordRoot, scale);

    if (pad !== 'none') {
      const voice = PAD_VOICES[pad];
      const low = deck.padRoot + 12 * voice.octave - 2;
      for (const d of tones) {
        const midi = foldInto(degreeToMidi(d, deck.padRoot + 12 * voice.octave, scale), low, 16);
        playVoice(ctx, deck.out, voice, { hz: midiToHz(midi), time, dur: barS, velocity: PART_VELOCITY.pad });
      }
      this.renderBass(deck, chordRoot, time, barS * 0.6, PART_VELOCITY.bass);
    } else {
      this.renderBass(deck, chordRoot, time, barS * 0.4, PART_VELOCITY.bassNoPad);
    }

    if (withTension) {
      const fifth = tones[tones.length - 1] ?? chordRoot;
      const midi = foldInto(degreeToMidi(fifth, deck.padRoot + 12, scale), deck.padRoot + 10, 12);
      playVoice(ctx, this.g.intensityIn, TENSION_PAD_VOICE, { hz: midiToHz(midi), time, dur: barS, velocity: PART_VELOCITY.tensionPad });
    }
  }

  private renderBass(deck: Deck, chordRoot: number, time: number, dur: number, velocity: number): void {
    const midi = foldInto(degreeToMidi(chordRoot, deck.bassRoot, deck.audio.scale), deck.bassRoot - 2, 11);
    playVoice(this.g.ctx, deck.out, BASS_VOICE, { hz: midiToHz(midi), time, dur, velocity });
  }
}
